import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { Mic } from 'lucide-react';

type MiniMicVisualizerProps = {
  stream: MediaStream | null;
  barCount?: number;
  className?: string;
  muteThreshold?: number; // default: 0.02
  muteDelayMs?: number; // default: 2000ms
  onMuteStateChange?: (isMuted: boolean) => void;
};

const MiniMicVisualizer: FC<MiniMicVisualizerProps> = ({
  stream,
  barCount = 49,
  className = '',
  muteThreshold = 0.02,
  muteDelayMs = 2000,
  onMuteStateChange,
}) => {
  const [volume, setVolume] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const lastLoudTimeRef = useRef<number>(0);
  const animationRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const bufferLengthRef = useRef<number>();
  const audioContextRef = useRef<AudioContext>();

  const isStreamActive = stream?.active === true;

  // Notify parent component when mute state changes
  useEffect(() => {
    onMuteStateChange?.(isMuted);
  }, [isMuted, onMuteStateChange]);

  useEffect(() => {
    // if there's no active audio stream, reset the state of our visualizer and exit early
    if (!isStreamActive) {
      setVolume(0);
      setIsMuted(true);
      return;
    }

    // create the audio context (used to analyze audio in real-time)
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    audioContextRef.current = audioContext;

    // create an analyser node to process the audio stream
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512; // smaller fftSize = faster + more responsive, but less accurate

    // connect the MediaStream (e.g. microphone audio) to the analyser
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    // prepare a buffer to hold the time-domain waveform data
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength) as Uint8Array<ArrayBuffer>;

    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;
    bufferLengthRef.current = bufferLength;

    // the main animation loop that continuously samples mic input
    const update = () => {
      const analyser = analyserRef.current;
      const dataArray = dataArrayRef.current;
      const bufferLength = bufferLengthRef.current;

      if (!analyser || !dataArray || bufferLength === undefined) return;

      // fill dataArray with the current waveform from the analyser
      // Type assertion needed because getByteTimeDomainData expects Uint8Array<ArrayBuffer>
      analyser.getByteTimeDomainData(dataArray as Uint8Array<ArrayBuffer>);

      // compute the peak volume and RMS (root mean square) to detect sound level
      let peak = 0;
      let sumSquares = 0;

      // TypeScript guard: dataArray is checked above, but we need to assert it's not null
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const safeDataArray: Uint8Array<ArrayBuffer> = dataArray!;
      for (let i = 0; i < bufferLength; i++) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const v = safeDataArray[i]! - 128; // convert to range around 0
        const normalized = Math.abs(v) / 128;
        peak = Math.max(peak, normalized); // track the loudest single value
        sumSquares += v * v;
      }

      // calculate RMS volume to detect if the input is silent or not
      const rms = Math.sqrt(sumSquares / bufferLength) / 128;
      const now = performance.now();

      // if the sound exceeds the mute threshold, update the "last loud" timestamp
      if (rms >= muteThreshold) {
        lastLoudTimeRef.current = now;
      }

      // if enough time has passed without sound, mark as muted
      const isCurrentlyMuted = now - lastLoudTimeRef.current > muteDelayMs;

      // update React state: volume for bar rendering, muted for UI status
      setVolume(peak);
      setIsMuted(isCurrentlyMuted);

      // loop: schedule the next animation frame
      animationRef.current = requestAnimationFrame(update);
    };

    // kick off the loop immediately on mount
    lastLoudTimeRef.current = performance.now();
    update();

    // cleanup when the stream stops or component unmounts
    return () => {
      cancelAnimationFrame(animationRef.current!); // stop loop
      analyser.disconnect();
      source.disconnect();
      audioContext.close(); // close the audio context to free system resources
    };
  }, [isStreamActive, stream, muteThreshold, muteDelayMs]);

  const barColor = isMuted ? 'vai-bg-red-500' : 'vai-bg-green-500';

  /**
   * Tailwind CSS (even with JIT) requires finite values.
   * Since getBarHeight(i) produces dynamic/continuous values, we need to:
   * - Quantize (round) the height into a discrete set
   * - Map those to predefined Tailwind classes
   */
  const barHeightClasses = [
    'vai-h-[8px]',
    'vai-h-[16px]',
    'vai-h-[24px]',
    'vai-h-[32px]',
    'vai-h-[40px]',
  ];

  const getBarHeightClass = (index: number, currentVolume: number): string => {
    const center = Math.floor(barCount / 2);
    const distance = Math.abs(center - index);
    const scale = 1 - distance / center;
    const effectiveVolume = Math.min(1, currentVolume * scale);
    const heightIndex = Math.round(
      effectiveVolume * (barHeightClasses.length - 1),
    );
    // Ensure heightIndex is within bounds
    const clampedIndex = Math.max(
      0,
      Math.min(heightIndex, barHeightClasses.length - 1),
    );
    // TypeScript should know this is safe, but we'll assert it
    const result = barHeightClasses[clampedIndex];
    if (result === undefined) {
      return barHeightClasses[0]!;
    }
    return result;
  };

  return (
    <div
      className={classNames('vai-flex vai-items-center vai-gap-2', className)}
    >
      {/* Mic Icon */}
      <Mic
        className={classNames(
          'vai-w-4 vai-h-4 vai-text-muted-foreground',
          !isMuted && 'vai-animate-pulse vai-text-green-500',
        )}
      />

      {/* Bars */}
      <div className="vai-flex vai-items-center vai-justify-center vai-gap-[4px] vai-h-[40px]">
        {Array.from({ length: barCount }, (_, i) => {
          return (
            <div
              key={i}
              className={classNames(
                'vai-w-[4px] vai-rounded-sm vai-transition-all vai-duration-75 vai-ease-out',
                barColor,
                getBarHeightClass(i, volume),
              )}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MiniMicVisualizer;
